/** The signed-in user's profile: identity, roles, language, password change. */
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useSession } from '../lib/session.tsx';
import { useRoute } from '../lib/router.tsx';
import { Badge, Button, Card, Field, Kv, Tabs, useToast } from '../components/ui.tsx';
import { PageHead } from '../components/PageHead.tsx';

export function ProfilePage() {
  const { t, locale } = useI18n();
  const { session, setLocale, logout } = useSession();
  const { query, setQuery } = useRoute();
  const toast = useToast();
  const tab = query.get('tab') ?? 'profile';
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const change = async () => {
    if (pw.next !== pw.confirm) { toast.error(t('ui.passwordMismatch')); return; }
    setBusy(true);
    try { await api.post('auth/password', { currentPassword: pw.current, newPassword: pw.next }); toast.success(t('auth.passwordChanged')); await logout(); }
    catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  if (!session) return null;
  return (
    <div className="stack">
      <PageHead title={session.user.fullName} sub={session.user.email} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v === 'profile' ? null : v })} tabs={[{ value: 'profile', label: t('auth.profile') }, { value: 'password', label: t('auth.changePassword') }]} />
      {tab === 'profile' && (
        <div className="two-col">
          <Card title={t('auth.profile')}>
            <Kv items={[{ label: t('common.name'), value: session.user.fullName }, { label: t('auth.email'), value: <span dir="ltr">{session.user.email}</span> }, { label: t('ui.jobTitle'), value: session.jobTitle ?? '—' }, { label: 'Company', value: session.tenant?.name }, { label: t('admin.roles'), value: <span className="chips">{session.roles.map((r) => <Badge key={r.code} color="teal">{locale === 'ar' && r.nameAr ? r.nameAr : r.name}</Badge>)}{session.isOwner && <Badge color="purple">owner</Badge>}</span> }, { label: t('ui.timezone'), value: session.user.timezone }]} />
          </Card>
          <Card title={t('auth.language')}>
            <div className="row"><Button variant={locale === 'en' ? 'primary' : 'default'} onClick={() => void setLocale('en')}>English</Button><Button variant={locale === 'ar' ? 'primary' : 'default'} onClick={() => void setLocale('ar')}>العربية</Button></div>
            <p className="muted">{t('ui.languageHint')}</p>
          </Card>
        </div>
      )}
      {tab === 'password' && (
        <Card title={t('auth.changePassword')}>
          <div className="fields" style={{ maxWidth: 560 }}>
            <Field label={t('auth.currentPassword')} required span={2}><input className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></Field>
            <Field label={t('auth.newPassword')} required hint={t('ui.passwordPolicy')}><input className="input" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
            <Field label={t('auth.confirmPassword')} required><input className="input" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></Field>
          </div>
          <div className="row mt"><Button variant="primary" icon="KeyRound" busy={busy} disabled={!pw.current || !pw.next} onClick={() => void change()}>{t('auth.changePassword')}</Button></div>
        </Card>
      )}
    </div>
  );
}
